import { createRouter, createWebHistory } from "vue-router";
import MainFormPage from "../pages/MainFormPage.vue";
import LoginPage from "@/pages/LoginPage.vue";
import FirstFormPage from "@/pages/FirstFormPage.vue";
import SecondFormPage from "@/pages/SecondFormPage.vue";
import ThirdFormPage from "@/pages/ThirdFormPage.vue";
import HomePage from "@/pages/HomePage.vue";
import authService, { currentUserIsAdmin } from "../services/authService";
import { useRouteStore } from "@/stores/ApplicationStore";
import ConfirmationPage from "@/pages/ConfirmationPage.vue";
import DatasetList from "@/pages/DatasetListPage.vue";
import DatasetPage from "@/pages/DatasetPage.vue";
import DashboardPage from "@/pages/DashboardPage.vue";
import AdminLogsPage from "@/pages/AdminLogsPage.vue";
import EditMyUpload from "@/pages/EditMyUpload.vue";
import AddDatasetPage from "@/pages/AddDatasetPage.vue";
import { useFormStore } from "@/stores/FormStore";

const routes = [
  {
    path: "/",
    name: "HomePage",
    components: {
      main: HomePage,
    },
    meta: { requiresAuth: false }, // Always accessible
  },
  {
    path: "/show",
    name: "showList",
    components: {
      main: DatasetList, // this shows when path is /show
    },
    meta: { requiresAuth: false }, // Always accessible
  },
  {
    path: "/myUploads",
    name: "MyUploads",
    components: {
      main: EditMyUpload, // this shows when path is /show
    },
    meta: { requiresAuth: true }, // Always accessible
  },
  {
    path: "/myUploads/:investigation_id",
    name: "myUploadDataset",
    components: {
      main: DatasetPage, // same detail page as /show/:id, kept under My Uploads
    },
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: "/myUploads/:investigation_id/dataset/new",
    name: "AddDataset",
    components: {
      main: AddDatasetPage,
    },
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: "/show/:investigation_id",
    name: "showDataset",
    components: {
      main: DatasetPage, // this shows when path is /show
    },
    meta: { requiresAuth: false }, // Always accessible
    props: true,
  },
  {
    path: "/form",
    name: "FormPage",
    components: {
      main: MainFormPage,
    },
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "new",
        meta: { number: 1 },
        component: FirstFormPage, // or a welcome/default component
      },
      {
        path: "new",
        name: "new",
        meta: { number: 1 },
        component: FirstFormPage,
      },
      {
        path: "extra",
        name: "extra",
        meta: { number: 2 },
        component: SecondFormPage,
      },
      {
        path: "submit",
        name: "submit",
        meta: { number: 3 },
        component: ThirdFormPage,
      },
      {
        path: "/confirmation",
        name: "confirmation",
        meta: { number: 4 },
        component: ConfirmationPage,
      },
    ],
  },

  // Admin Routes
  {
    path: "/admin",
    name: "AdminDashboard",
    components: {
      main: DashboardPage,
    },
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/admin/logs",
    name: "AdminLogs",
    components: {
      main: AdminLogsPage,
    },
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  {
    path: "/login",
    name: "Login",
    components: {
      main: LoginPage,
    },
    meta: { requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.VUE_APP_PROXY_PATH || "/enodo/"),
  routes,
});

let visitedPages = {
  new: false,
  extra: false,
  submit: false,
};

const defaultTitle = "Enodo";

router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title || defaultTitle;

  const routeStore = useRouteStore();
  routeStore.setPreviousRoute({
    name: from.name,
    path: from.path,
    params: from.params,
    query: from.query,
  });

  try {
    // Use the reactive currentUser directly - no API call
    const verifiedUser = await authService.isAuthenticated();
    const isAuthenticated = !!verifiedUser;
    const path_pieces = to.fullPath.split("/");

    if (to.meta.requiresAuth && !isAuthenticated) {
      return next({ path: "/login", query: { redirect: to.fullPath } });
    }

    if (to.name === "Login" && isAuthenticated) {
      return next({ path: "/" });
    }

    // Check admin requirements
    if (to.meta.requiresAdmin && isAuthenticated) {
      const isAdmin = currentUserIsAdmin.value;
      if (!isAdmin) {
        // User is not admin, redirect to home page with access denied
        return next({ path: "/", query: { accessDenied: "admin" } });
      }
    }

    if (path_pieces.includes("form")) {
      const lastPiece = path_pieces[path_pieces.length - 1];

      if (
        ["extra", "submit"].includes(lastPiece) &&
        from.name === "confirmation"
      ) {
        if (from?.query?.allowBack === "1") {
          return next();
        }
        return next(false);
      }

      if (!from.name && to.path === "/form") {
        return next({ path: "/form/new" });
      }

      if (to.name === "new") {
        const formStore = useFormStore();

        // Are we coming from a form step?
        const formSteps = new Set(["new", "extra", "submit", "confirmation"]);
        const fromIsFormStep = formSteps.has(from?.name);

        // Reset when:
        // - coming from outside the form flow (fresh start), OR
        // - coming from the completed page (confirmation)
        // Do NOT reset when coming back from 'extra'.
        const shouldReset = !fromIsFormStep || from.name === "confirmation";

        if (shouldReset) {
          formStore.resetAll?.(); // safely call if available on your upgraded store
          visitedPages = { new: false, extra: false, submit: false };
        }

        // mark step as visited (same behavior as before)
        visitedPages.new = true;

        // if we *did* come from 'extra', explicitly keep its state (no reset)
        if (from.name === "extra") {
          visitedPages.extra = false; // keep your previous logic if desired
        }

        return next();
      }

      if (to.name === "extra") {
        if (visitedPages.new) {
          visitedPages.extra = true;
          return next();
        }
        if (from.name === "submit") {
          visitedPages.submit = false;
          return next();
        }
        return next({ name: "new" });
      }

      if (to.name === "submit") {
        if (visitedPages.new && visitedPages.extra) {
          visitedPages.submit = true;
          return next();
        }
        return next({ name: "new" });
      }

      if (to.name === "confirmation") {
        if (visitedPages.submit) {
          return next();
        }
        return next({ name: "new" });
      }

      // fallback in form routes
      return next();
    }

    if (path_pieces.includes("show")) {
      return next();
    }

    // if (to.name === "HomePage") {
    //   return next({ path: "form/new" });
    // }

    // default allow
    return next();
  } catch (error) {
    console.error("Authentication check failed:", error);
    return next({ name: "Login" });
  }
});

export default router;
